import type {
  Template,
  OutputFormat,
  LabelElement,
  TextElement,
  ImageElement,
  QrElement,
  BarcodeElement,
  ShapeElement,
} from './types';
import { getQrTemplateGeometry, QR_DESIGN_PAYLOAD } from './qrSizing';

/**
 * Converts a template's pixel-based geometry from one DPI to another.
 * Used when cloning a template into a different output format/DPI so that
 * the physical label dimensions and element positions are preserved.
 */
export function convertTemplateDpi(
  source: Template,
  targetDpi: number,
  targetFormat: OutputFormat,
): Template {
  const sourceDpi = source.dpi ?? 72;
  const scale = targetDpi / sourceDpi;

  // No geometry conversion needed if DPI is unchanged
  if (scale === 1) {
    return {
      ...source,
      outputFormat: targetFormat,
      dpi: targetDpi,
      // A format switch at the same DPI can still receive a legacy unsafe QR.
      // Normalize QR geometry without rounding unrelated element geometry.
      elements: source.elements.map((element) =>
        element.type === 'qr' ? scaleQrElement(element, 1, targetDpi, targetFormat) : element,
      ),
    };
  }

  return {
    ...source,
    outputFormat: targetFormat,
    dpi: targetDpi,
    width: Math.round(source.width * scale),
    height: Math.round(source.height * scale),
    elements: source.elements.map((el) => scaleElement(el, scale, targetDpi, targetFormat)),
  };
}

function scaleElement(element: LabelElement, scale: number, targetDpi: number, targetFormat: OutputFormat): LabelElement {
  switch (element.type) {
    case 'text':
      return scaleTextElement(element, scale);
    case 'image':
      return scaleImageElement(element, scale);
    case 'qr':
      return scaleQrElement(element, scale, targetDpi, targetFormat);
    case 'barcode':
      return scaleBarcodeElement(element, scale);
    case 'shape':
      return scaleShapeElement(element, scale);
    default:
      return element;
  }
}

function scaleTextElement(el: TextElement, scale: number): TextElement {
  return {
    ...el,
    x: Math.round(el.x * scale),
    y: Math.round(el.y * scale),
    fontSize: Math.round(el.fontSize * scale),
    width: Math.round(el.width * scale),
  };
}

function scaleImageElement(el: ImageElement, scale: number): ImageElement {
  return {
    ...el,
    x: Math.round(el.x * scale),
    y: Math.round(el.y * scale),
    width: Math.round(el.width * scale),
    height: Math.round(el.height * scale),
  };
}

function scaleQrElement(el: QrElement, scale: number, targetDpi: number, targetFormat: OutputFormat): QrElement {
  const requestedSize = Math.round(Math.min(el.width, el.height) * scale);
  const margin = Math.round(el.margin * scale);
  const size = getQrTemplateGeometry({
    value: el.data || QR_DESIGN_PAYLOAD,
    requestedSize,
    dpi: targetDpi,
    outputFormat: targetFormat,
    margin,
    errorCorrectionLevel: el.errorCorrectionLevel,
  }).size;
  return {
    ...el,
    x: Math.round(el.x * scale),
    y: Math.round(el.y * scale),
    width: size,
    height: size,
    margin,
    ...(el.imageWidth != null ? { imageWidth: Math.round(el.imageWidth * scale) } : {}),
  };
}

function scaleBarcodeElement(el: BarcodeElement, scale: number): BarcodeElement {
  return {
    ...el,
    x: Math.round(el.x * scale),
    y: Math.round(el.y * scale),
    height: Math.round(el.height * scale),
    margin: Math.round(el.margin * scale),
    // barWidth is a multiplier (0.5-3), not a pixel value — do not scale
  };
}

function scaleShapeElement(el: ShapeElement, scale: number): ShapeElement {
  return {
    ...el,
    x: Math.round(el.x * scale),
    y: Math.round(el.y * scale),
    strokeWidth: Math.round(el.strokeWidth * scale),
    v1: { x: Math.round(el.v1.x * scale), y: Math.round(el.v1.y * scale) },
    v2: { x: Math.round(el.v2.x * scale), y: Math.round(el.v2.y * scale) },
    ...(el.v3 ? { v3: { x: Math.round(el.v3.x * scale), y: Math.round(el.v3.y * scale) } } : {}),
    ...(el.v4 ? { v4: { x: Math.round(el.v4.x * scale), y: Math.round(el.v4.y * scale) } } : {}),
    ...(el.width != null ? { width: Math.round(el.width * scale) } : {}),
    ...(el.height != null ? { height: Math.round(el.height * scale) } : {}),
  };
}
